import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { aiApi } from "../../api.js";
import { useAIStore } from "../../stores/aiStore.js";
import { useEditorStore } from "../../stores/editorStore.js";
import { toast } from "../Toast.js";
import { Button } from "../ui/Button.js";
import { PatchPreview } from "./PatchPreview.js";

const DEFAULT_PANEL_WIDTH = 390;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 720;
const MAIN_CONTENT_MIN_WIDTH = 280;
const PANEL_WIDTH_STORAGE_KEY = "aiPanelWidth";

function clampPanelWidth(nextWidth: number): number {
  const viewportMaxWidth = window.innerWidth - MAIN_CONTENT_MIN_WIDTH;
  const maxWidth = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, viewportMaxWidth));
  return Math.min(Math.max(nextWidth, MIN_PANEL_WIDTH), maxWidth);
}

function readStoredPanelWidth(): number {
  const stored = Number(localStorage.getItem(PANEL_WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampPanelWidth(stored) : DEFAULT_PANEL_WIDTH;
}

export function AIPanel() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const docType = searchParams.get("type") ?? "judicial_invoice";
  const [text, setText] = useState("");
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef({ x: 0, width: DEFAULT_PANEL_WIDTH });
  const isPanelOpen = useAIStore((state) => state.isPanelOpen);
  const width = useAIStore((state) => state.panelWidth);
  const setWidth = useAIStore((state) => state.setPanelWidth);
  const closePanel = useAIStore((state) => state.closePanel);
  const addMessage = useAIStore((state) => state.addMessage);
  const isStreaming = useAIStore((state) => state.isStreaming);
  const setStreaming = useAIStore((state) => state.setStreaming);
  const messages = useAIStore((state) => state.messages);
  const pendingPatch = useAIStore((state) => state.pendingPatch);
  const setPendingPatch = useAIStore((state) => state.setPendingPatch);
  const conversation = useAIStore((state) => state.conversation);
  const addConversationTurn = useAIStore((state) => state.addConversationTurn);
  const clearConversation = useAIStore((state) => state.clearConversation);
  const templateId = useEditorStore((state) => state.templateId);
  const draft = useEditorStore((state) => state.draft);
  const selectedBlockId = useEditorStore((state) => state.selectedBlockId);

  useEffect(() => {
    setWidth(readStoredPanelWidth());
  }, [setWidth]);

  useEffect(() => {
    if (!isResizing) return undefined;

    function handlePointerMove(event: PointerEvent) {
      const delta = resizeStart.current.x - event.clientX;
      setWidth(clampPanelWidth(resizeStart.current.width + delta));
    }

    function handlePointerUp() {
      setIsResizing(false);
    }

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isResizing]);

  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(width));
  }, [width]);

  useEffect(() => {
    function handleWindowResize() {
      setWidth(clampPanelWidth(width));
    }

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [setWidth, width]);

  if (!isPanelOpen) return null;

  async function send() {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;
    if (!draft) {
      toast("編集中のテンプレートがありません", "error");
      return;
    }

    addMessage({ role: "user", content: trimmed });
    addConversationTurn({ role: "user", content: trimmed });
    setText("");
    setStreaming(true);

    try {
      const res = await aiApi.templateEdit({
        instruction: trimmed,
        selected_block_id: selectedBlockId,
        template: draft as unknown as Record<string, unknown>,
        template_id: templateId,
        doc_type: docType,
        conversation,
      });

      if (res.type === "clarify") {
        // 情報不足でヒアリング中: パッチは作らず、質問をチャットに積んで回答待ちにする
        addMessage({ role: "assistant", content: res.question });
        addConversationTurn({ role: "assistant", content: res.question });
      } else {
        setPendingPatch(res.patch, res.summary);
        addMessage({ role: "assistant", content: res.summary || "変更案を作成しました。内容をご確認のうえ、よろしければ「適用する」を押してください。" });
        clearConversation();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "AI提案の作成に失敗しました";
      toast(message, "error");
      addMessage({ role: "assistant", content: `申し訳ございません、変更案の作成に失敗しました。少し言い方を変えて、もう一度お試しください。\n（詳細: ${message}）` });
    } finally {
      setStreaming(false);
    }
  }

  return (
    <aside style={{ position: "fixed", top: 0, right: 0, width: `min(${width}px, 100vw)`, height: "100vh", zIndex: 50, background: "#fff", borderLeft: "1px solid #cbd5e1", boxShadow: "-12px 0 28px rgba(15,23,42,0.12)", display: "flex", flexDirection: "column" }}>
      <div
        aria-label="AIアシスタントの幅を調整"
        role="separator"
        title="ドラッグして幅を調整"
        onPointerDown={(event) => {
          resizeStart.current = { x: event.clientX, width };
          setIsResizing(true);
        }}
        style={{
          position: "absolute",
          top: 0,
          left: -5,
          width: 10,
          height: "100%",
          cursor: "col-resize",
          touchAction: "none",
          zIndex: 1,
        }}
      >
        <div style={{ width: 2, height: "100%", margin: "0 auto", background: isResizing ? "#14b8a6" : "transparent" }} />
      </div>
      <div style={{ height: 56, padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0" }}>
        <div>
          <div style={{ fontWeight: 900, color: "#0f172a" }}>AI アシスタント</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{location.pathname}</div>
        </div>
        <Button onClick={closePanel}>閉じる</Button>
      </div>

      <div style={{ display: "flex", gap: 6, padding: 12, borderBottom: "1px solid #e2e8f0" }}>
        {["チャット", "操作履歴", "テンプレ生成"].map((label) => (
          <button key={label} style={{ border: "none", background: label === "チャット" ? "#f0fdfa" : "transparent", color: label === "チャット" ? "#0f766e" : "#64748b", borderRadius: 7, padding: "7px 10px", fontSize: 12, fontWeight: 800 }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.length === 0 && (
          <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {"テンプレートの修正、項目追加、数式の相談ができます。生成された差分は適用前にご確認いただけますので、お気軽にお試しください。\n\n例:\n・「振込先の下に備考欄を追加して」\n・「消費税の行を太字にして」\n・「宛名を右揃えにして」"}
          </div>
        )}
        {messages.map((message) => (
          <div key={message.id} style={{ alignSelf: message.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%", background: message.role === "user" ? "#0f766e" : "#f8fafc", color: message.role === "user" ? "#fff" : "#0f172a", borderRadius: 10, padding: "9px 11px", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
            {message.content}
          </div>
        ))}
        {pendingPatch && <PatchPreview patch={pendingPatch} />}
      </div>

      <div style={{ borderTop: "1px solid #e2e8f0", padding: 12, display: "flex", gap: 8 }}>
        <input
          value={text}
          disabled={isStreaming}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter" && !event.nativeEvent.isComposing) void send(); }}
          placeholder="メッセージを入力..."
          style={{ flex: 1, height: 36, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13, opacity: isStreaming ? 0.7 : 1 }}
        />
        <Button variant="primary" onClick={() => void send()} disabled={isStreaming || !text.trim()}>
          {isStreaming ? "送信中..." : "送信"}
        </Button>
      </div>
    </aside>
  );
}
